import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { EMAIL_PROVIDERS, detectEmailProvider, type EmailAccount } from "@/types/email";
import { Mail, Plus, Settings, Trash, Eye, EyeOff, CheckCircle, AlertCircle, Edit } from "lucide-react";

interface EmailAccountSetupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialAccount?: EmailAccount | null;
}

export const EmailAccountSetup = ({ open, onOpenChange, initialAccount }: EmailAccountSetupProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [activeTab, setActiveTab] = useState("accounts");
  const [showPassword, setShowPassword] = useState(false);
  const [detectedProvider, setDetectedProvider] = useState<string | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [editingAccount, setEditingAccount] = useState<EmailAccount | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [formData, setFormData] = useState({
    email_address: "",
    display_name: "",
    provider: "custom",
    smtp_host: "",
    smtp_port: 587,
    smtp_username: "",
    smtp_password: "",
    imap_host: "",
    imap_port: 993,
    imap_username: "",
    is_default: false,
  });

  useEffect(() => {
    if (open && user) {
      loadEmailAccounts();
      // Wenn ein initialAccount übergeben wurde, direkt in den Bearbeitungsmodus
      if (initialAccount) {
        setEditingAccount(initialAccount);
        setFormData({
          email_address: initialAccount.email_address,
          display_name: initialAccount.display_name || "",
          provider: initialAccount.provider,
          smtp_host: initialAccount.smtp_host || "",
          smtp_port: initialAccount.smtp_port || 587,
          smtp_username: initialAccount.smtp_username || "",
          smtp_password: "",
          imap_host: initialAccount.imap_host || "",
          imap_port: initialAccount.imap_port || 993,
          imap_username: initialAccount.imap_username || "",
          is_default: initialAccount.is_default || false,
        });
        setManualMode(true);
        setActiveTab("add");
      }
    }
  }, [open, user, initialAccount]);

  const loadEmailAccounts = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('email_accounts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAccounts(data as EmailAccount[] || []);
    } catch (error) {
      console.error('Error loading email accounts:', error);
      toast({
        title: "Fehler",
        description: "E-Mail-Konten konnten nicht geladen werden.",
        variant: "destructive",
      });
    }
  };

  // Automatische Provider-Erkennung bei E-Mail-Eingabe
  const handleEmailChange = async (email: string) => {
    setFormData(prev => ({ ...prev, email_address: email }));
    
    if (email.includes('@')) {
      setLoading(true);
      
      try {
        // Rufe Server-Discovery Edge Function auf
        const { data, error } = await supabase.functions.invoke('email-server-discovery', {
          body: { email }
        });

        if (error) throw error;

        if (data.oauth_available) {
          // OAuth-Provider erkannt (Google/Microsoft)
          setDetectedProvider(data.provider);
          setManualMode(false);
          
          setFormData(prev => ({
            ...prev,
            provider: data.provider.toLowerCase(),
            smtp_host: data.smtp_host,
            smtp_port: data.smtp_port,
            imap_host: data.imap_host,
            imap_port: data.imap_port,
            smtp_username: email,
            imap_username: email,
          }));

          toast({
            title: `${data.provider} erkannt`,
            description: "Sie können sich direkt mit OAuth anmelden oder Passwort verwenden.",
          });
        } else {
          // Standard-Provider mit automatischer Konfiguration
          setDetectedProvider(data.provider);
          setManualMode(false);
          
          setFormData(prev => ({
            ...prev,
            provider: 'custom',
            smtp_host: data.smtp_host,
            smtp_port: data.smtp_port,
            imap_host: data.imap_host,
            imap_port: data.imap_port,
            smtp_username: email,
            imap_username: email,
          }));

          toast({
            title: "Server-Einstellungen gefunden",
            description: `Automatische Konfiguration für ${data.provider} geladen.`,
          });
        }
      } catch (error) {
        console.error('Server-Discovery fehler:', error);
        setDetectedProvider(null);
        setManualMode(true);
        
        toast({
          title: "Server nicht gefunden",
          description: "Bitte geben Sie die Server-Einstellungen manuell ein.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleProviderChange = (provider: string) => {
    setFormData(prev => ({
      ...prev,
      provider,
      ...EMAIL_PROVIDERS[provider]
    }));
  };

  const testConnection = async () => {
    if (!formData.email_address || !formData.smtp_password) {
      toast({
        title: "Fehlende Daten",
        description: "E-Mail-Adresse und Passwort sind erforderlich für den Verbindungstest.",
        variant: "destructive",
      });
      return false;
    }

    setTestingConnection(true);
    try {
      console.log('Starting connection test for:', formData.email_address);
      console.log('Test data:', {
        email_address: formData.email_address,
        imap_host: formData.imap_host,
        imap_port: formData.imap_port,
        smtp_host: formData.smtp_host,
        smtp_port: formData.smtp_port,
      });

      const { data, error } = await supabase.functions.invoke('sync-emails', {
        body: { 
          testOnly: true,
          account: {
            email_address: formData.email_address,
            imap_host: formData.imap_host,
            imap_port: formData.imap_port,
            imap_username: formData.imap_username || formData.email_address,
            imap_password: formData.smtp_password,
            smtp_host: formData.smtp_host,
            smtp_port: formData.smtp_port,
            smtp_username: formData.smtp_username || formData.email_address,
            smtp_password: formData.smtp_password,
          }
        }
      });

      console.log('Function response:', { data, error });

      if (error) {
        console.error('Function error details:', error);
        // Try to parse error message for AUTH_FAILED
        let parsedError: any = null;
        try { parsedError = JSON.parse(error.message || '{}'); } catch {}
        
        if (parsedError?.code === 'AUTH_FAILED' || error.message?.includes('AUTH_FAILED') || error.message?.includes('Passwort')) {
          toast({
            title: "Anmeldung fehlgeschlagen",
            description: "Benutzername oder Passwort ist falsch. Bitte überprüfen Sie Ihre Zugangsdaten.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Verbindungstest fehlgeschlagen",
            description: parsedError?.message || `Fehler: ${error.message || 'Unbekannter Fehler'}`,
            variant: "destructive",
          });
        }
        return false;
      }

      if (data && data.success) {
        toast({
          title: "Verbindung erfolgreich",
          description: "Die E-Mail-Server-Verbindung wurde erfolgreich getestet.",
        });
        return true;
      } else {
        const errorMessage = data?.message || data?.error || "Die Verbindung zum E-Mail-Server konnte nicht hergestellt werden.";
        // Check specifically for auth failures
        if (data?.code === 'AUTH_FAILED' || errorMessage.toLowerCase().includes('passwort') || errorMessage.toLowerCase().includes('login')) {
          toast({
            title: "Anmeldung fehlgeschlagen",
            description: "Benutzername oder Passwort ist falsch. Bitte überprüfen Sie Ihre Zugangsdaten.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Verbindung fehlgeschlagen",
            description: errorMessage,
            variant: "destructive",
          });
        }
        return false;
      }
    } catch (error) {
      console.error('Connection test error:', error);
      toast({
        title: "Verbindungstest fehlgeschlagen",
        description: `Fehler beim Aufruf der Verbindungstest-Funktion: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`,
        variant: "destructive",
      });
      return false;
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Teste zuerst die Verbindung
    const connectionSuccessful = await testConnection();
    if (!connectionSuccessful) {
      return; // Breche ab, wenn die Verbindung fehlschlägt
    }

    setLoading(true);
    try {
      // Get user's organization
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.organization_id) {
        throw new Error('Benutzer ist keiner Organisation zugeordnet');
      }

      // Prepare data with imap_password set from smtp_password
      const accountData = {
        email_address: formData.email_address,
        display_name: formData.display_name || null,
        provider: formData.provider,
        smtp_host: formData.smtp_host || null,
        smtp_port: formData.smtp_port,
        smtp_username: formData.smtp_username || formData.email_address,
        smtp_password: formData.smtp_password,
        imap_host: formData.imap_host || null,
        imap_port: formData.imap_port,
        imap_username: formData.imap_username || formData.email_address,
        imap_password: formData.smtp_password, // IMPORTANT: Use the same password for IMAP
        is_default: formData.is_default,
      };

      if (editingAccount) {
        // Update existing account - only include password if provided
        const updateData: any = {
          ...accountData,
          updated_at: new Date().toISOString(),
        };
        
        // If no password was entered during edit, don't overwrite the existing one
        if (!formData.smtp_password) {
          delete updateData.smtp_password;
          delete updateData.imap_password;
        }

        const { error } = await supabase
          .from('email_accounts')
          .update(updateData)
          .eq('id', editingAccount.id);

        if (error) throw error;

        toast({
          title: "Erfolg",
          description: "E-Mail-Konto wurde erfolgreich aktualisiert.",
        });
      } else {
        // Create new account
        const { error } = await supabase
          .from('email_accounts')
          .insert({
            user_id: user.id,
            organization_id: profile.organization_id,
            ...accountData,
            is_active: true,
          });

        if (error) throw error;

        toast({
          title: "Erfolg",
          description: "E-Mail-Konto wurde erfolgreich hinzugefügt.",
        });
      }

      // Reset form
      resetForm();
      loadEmailAccounts();
      setActiveTab("accounts");
    } catch (error) {
      console.error('Error saving email account:', error);
      toast({
        title: "Fehler",
        description: "E-Mail-Konto konnte nicht gespeichert werden.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email_address: "",
      display_name: "",
      provider: "custom",
      smtp_host: "",
      smtp_port: 587,
      smtp_username: "",
      smtp_password: "",
      imap_host: "",
      imap_port: 993,
      imap_username: "",
      is_default: false,
    });
    setDetectedProvider(null);
    setManualMode(false);
    setEditingAccount(null);
  };

  const startEdit = (account: EmailAccount) => {
    setEditingAccount(account);
    setFormData({
      email_address: account.email_address,
      display_name: account.display_name || "",
      provider: account.provider,
      smtp_host: account.smtp_host || "",
      smtp_port: account.smtp_port || 587,
      smtp_username: account.smtp_username || "",
      smtp_password: "", // Passwort nicht vorausfüllen aus Sicherheitsgründen
      imap_host: account.imap_host || "",
      imap_port: account.imap_port || 993,
      imap_username: account.imap_username || "",
      is_default: account.is_default || false,
    });
    setManualMode(true); // Beim Bearbeiten immer manuelle Eingabe ermöglichen
    setActiveTab("add");
  };

  const deleteAccount = async (accountId: string) => {
    try {
      const { error } = await supabase
        .from('email_accounts')
        .delete()
        .eq('id', accountId);

      if (error) throw error;

      toast({
        title: "Erfolg",
        description: "E-Mail-Konto wurde gelöscht.",
      });

      loadEmailAccounts();
    } catch (error) {
      console.error('Error deleting email account:', error);
      toast({
        title: "Fehler",
        description: "E-Mail-Konto konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto min-w-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            E-Mail-Konten verwalten
          </DialogTitle>
          <DialogDescription>
            Richten Sie Ihre E-Mail-Konten ein und verwalten Sie Ihre Verbindungen.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="accounts">Konten</TabsTrigger>
            <TabsTrigger value="add">Konto hinzufügen</TabsTrigger>
          </TabsList>

          <TabsContent value="accounts" className="space-y-4">
            <div className="grid gap-4">
              {accounts.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-8">
                    <Mail className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">Keine E-Mail-Konten konfiguriert</p>
                    <Button
                      className="mt-4"
                      onClick={() => setActiveTab("add")}
                    >
                      Erstes Konto hinzufügen
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                accounts.map((account) => (
                  <Card key={account.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{account.display_name || account.email_address}</CardTitle>
                          <CardDescription>{account.email_address}</CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          {account.is_default && (
                            <span className="bg-primary text-primary-foreground px-2 py-1 rounded text-xs">
                              Standard
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(account)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteAccount(account.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <Label className="text-muted-foreground">Provider</Label>
                          <p>{EMAIL_PROVIDERS[account.provider]?.name || account.provider}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Status</Label>
                          <p className={account.is_active ? "text-green-600" : "text-red-600"}>
                            {account.is_active ? "Aktiv" : "Inaktiv"}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="add" className="space-y-4">
            {editingAccount && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-medium text-blue-800 mb-1">
                  E-Mail-Konto bearbeiten
                </h4>
                <p className="text-xs text-blue-700">
                  Sie bearbeiten das Konto: {editingAccount.email_address}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={resetForm}
                >
                  Bearbeitung abbrechen
                </Button>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail-Adresse *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email_address}
                      onChange={(e) => handleEmailChange(e.target.value)}
                      placeholder="beispiel@provider.de"
                      disabled={!!editingAccount} // Beim Bearbeiten nicht änderbar
                      required
                    />
                  {/* Status-Anzeige der automatischen Erkennung */}
                  {formData.email_address.includes('@') && (
                    <div className="flex items-center gap-2 text-sm">
                      {detectedProvider ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-green-600">
                            Server-Einstellungen für {detectedProvider} automatisch erkannt
                          </span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="h-4 w-4 text-orange-600" />
                          <span className="text-orange-600">
                            Provider nicht erkannt - Manuelle Eingabe erforderlich
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="display_name">Anzeigename</Label>
                  <Input
                    id="display_name"
                    value={formData.display_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                    placeholder="Mein E-Mail-Konto"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="provider">E-Mail-Provider</Label>
                <Select 
                  value={formData.provider} 
                  onValueChange={handleProviderChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EMAIL_PROVIDERS).map(([key, provider]) => (
                      <SelectItem key={key} value={key}>
                        {provider.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(manualMode || formData.provider === 'custom') && (
                <>
                  <div className="p-4 border rounded-lg bg-muted/50">
                    <h4 className="text-sm font-medium mb-2">Manuelle Server-Konfiguration</h4>
                    <p className="text-xs text-muted-foreground mb-3">
                      Da Ihr E-Mail-Provider nicht automatisch erkannt wurde, geben Sie bitte die Server-Einstellungen manuell ein.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp_host">SMTP Server *</Label>
                      <Input
                        id="smtp_host"
                        value={formData.smtp_host}
                        onChange={(e) => setFormData(prev => ({ ...prev, smtp_host: e.target.value }))}
                        placeholder="smtp.example.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp_port">SMTP Port *</Label>
                      <Input
                        id="smtp_port"
                        type="number"
                        value={formData.smtp_port}
                        onChange={(e) => setFormData(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="imap_host">IMAP Server *</Label>
                      <Input
                        id="imap_host"
                        value={formData.imap_host}
                        onChange={(e) => setFormData(prev => ({ ...prev, imap_host: e.target.value }))}
                        placeholder="imap.example.com"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="imap_port">IMAP Port *</Label>
                      <Input
                        id="imap_port"
                        type="number"
                        value={formData.imap_port}
                        onChange={(e) => setFormData(prev => ({ ...prev, imap_port: parseInt(e.target.value) || 993 }))}
                        required
                      />
                    </div>
                  </div>
                </>
              )}

              {detectedProvider && !manualMode && (
                <div className="p-4 border rounded-lg bg-green-50 border-green-200">
                  <h4 className="text-sm font-medium text-green-800 mb-2">
                    Automatische Konfiguration: {detectedProvider}
                  </h4>
                  <p className="text-xs text-green-700">
                    Die Server-Einstellungen wurden automatisch konfiguriert. Sie müssen nur noch Ihr Passwort eingeben.
                  </p>
                  <div className="mt-2 text-xs text-muted-foreground">
                    <p>SMTP: {formData.smtp_host}:{formData.smtp_port}</p>
                    <p>IMAP: {formData.imap_host}:{formData.imap_port}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      setManualMode(true);
                      setDetectedProvider(null);
                    }}
                  >
                    Manuell konfigurieren
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="imap_username">IMAP-Benutzername</Label>
                  <Input
                    id="imap_username"
                    value={formData.imap_username}
                    onChange={(e) => setFormData(prev => ({ ...prev, imap_username: e.target.value }))}
                    placeholder={formData.email_address || "Meist gleich der E-Mail-Adresse"}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp_username">SMTP-Benutzername</Label>
                  <Input
                    id="smtp_username"
                    value={formData.smtp_username}
                    onChange={(e) => setFormData(prev => ({ ...prev, smtp_username: e.target.value }))}
                    placeholder={formData.email_address || "Meist gleich der E-Mail-Adresse"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Passwort *</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.smtp_password}
                      onChange={(e) => setFormData(prev => ({ ...prev, smtp_password: e.target.value }))}
                      placeholder={editingAccount ? "Neues Passwort eingeben" : "Ihr E-Mail-Passwort"}
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_default"
                  checked={formData.is_default}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_default: checked }))}
                />
                <Label htmlFor="is_default">Als Standard-Konto festlegen</Label>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                    Abbrechen
                  </Button>
                  <Button 
                    type="button" 
                    variant="secondary" 
                    onClick={testConnection}
                    disabled={testingConnection || !formData.email_address || !formData.smtp_password}
                  >
                    {testingConnection ? "Teste..." : "Verbindung testen"}
                  </Button>
                </div>
                <Button type="submit" disabled={loading || testingConnection}>
                  {loading ? "Wird gespeichert..." : editingAccount ? "Konto aktualisieren" : "Konto hinzufügen"}
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};